<template>
  <div class="an">
    <MainHeader v-if="!activeView || loader || error" />

    <!-- loading -->
    <div
      v-if="loader"
      class="an-state"
    >
      <b-spinner
        type="grow"
        variant="primary"
        label="Loading..."
      />
      <p>Loading analysis...</p>
    </div>

    <!-- error -->
    <div
      v-else-if="error"
      class="an-state an-state--error"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="42"
      />
      <h5>We could not load the analysis</h5>
      <p>Please check your connection and try again.</p>
      <button
        type="button"
        class="an-retry"
        @click="getAnalysis"
      >
        <feather-icon
          icon="RefreshCwIcon"
          size="15"
        />
        Retry
      </button>
    </div>

    <template v-else>
      <!-- landing: choose a view -->
      <transition
        name="an-fade"
        mode="out-in"
      >
        <div
          v-if="!activeView"
          key="landing"
          class="an-choices"
        >
          <div
            v-for="view in views"
            :key="view.key"
            class="an-choice"
            :class="`an-choice--${view.key}`"
            role="button"
            tabindex="0"
            @click="openView(view.key)"
            @keyup.enter="openView(view.key)"
          >
            <span class="an-choice__bubble an-choice__bubble--1" />
            <span class="an-choice__bubble an-choice__bubble--2" />

            <div class="an-choice__icon">
              <feather-icon
                :icon="view.icon"
                size="30"
              />
            </div>

            <h3>{{ view.title }}</h3>
            <p>{{ view.description }}</p>

            <div class="an-choice__stats">
              <div
                v-for="stat in view.stats"
                :key="stat.label"
                class="an-choice__stat"
              >
                <feather-icon
                  :icon="stat.icon"
                  size="16"
                />
                <strong>{{ stat.value }}</strong>
                <span>{{ stat.label }}</span>
              </div>
            </div>

            <span class="an-choice__cta">
              Open
              <feather-icon
                icon="ArrowRightIcon"
                size="16"
              />
            </span>
          </div>
        </div>

        <!-- selected view -->
        <div
          v-else
          :key="activeView"
          class="an-view"
        >
          <!-- each view draws its own header (from the route name), then the tabs below it -->
          <component
            :is="activeComponent"
            v-bind="activeProps"
          >
            <template #tabs>
              <div class="an-tabs">
                <button
                  v-for="view in views"
                  :key="view.key"
                  type="button"
                  class="an-tab"
                  :class="{ 'an-tab--active': activeView === view.key }"
                  @click="openView(view.key)"
                >
                  <feather-icon
                    :icon="view.icon"
                    size="16"
                  />
                  {{ view.title }}
                </button>
              </div>
            </template>
          </component>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'
import { formatNumber, toNumber } from '@/components/analysis-table/analysisTheme'
import RequestsAnalysis from './RequestsAnalysis.vue'
import UsersAnalysis from './UsersAnalysis.vue'
import MainHeader from '@/components/main-header/MainHeader.vue'

export default {
  components: {
    RequestsAnalysis,
    UsersAnalysis,
    MainHeader,
  },
  data() {
    return {
      requestsAnalysisData: [],
      usersAnalysisData: [],
      loader: false,
      error: false,
    }
  },
  computed: {
    // Which view is open comes from the router: /analysis (landing), requests-analysis, users-analysis.
    activeView() {
      return {
        'requests-analysis': 'requests',
        'users-analysis': 'users',
      }[this.$route.name] || null
    },
    activeComponent() {
      return this.activeView === 'requests' ? 'RequestsAnalysis' : 'UsersAnalysis'
    },
    activeProps() {
      return this.activeView === 'requests'
        ? { requestsAnalysisData: this.requestsAnalysisData }
        : { userAnalysisData: this.usersAnalysisData }
    },
    views() {
      const sources = this.requestsAnalysisData
      const engineers = this.usersAnalysisData
      const sum = (list, key) => list.reduce((acc, item) => acc + toNumber(item[key]), 0)

      return [
        {
          key: 'requests',
          title: 'Requests Analysis',
          icon: 'BarChart2Icon',
          description: 'See how requests are distributed across every source: volume, issued certificates, pending and late work.',
          stats: [
            { icon: 'DatabaseIcon', label: 'Sources', value: formatNumber(sources.length) },
            { icon: 'FileTextIcon', label: 'Requests', value: formatNumber(sum(sources, 'total_count')) },
            { icon: 'ClockIcon', label: 'Pending', value: formatNumber(sum(sources, 'total_pending')) },
          ],
        },
        {
          key: 'users',
          title: 'Users Analysis',
          icon: 'UsersIcon',
          description: 'Follow the workload of every engineer, filter by source and quickly spot who has late requests.',
          stats: [
            { icon: 'UsersIcon', label: 'Engineers', value: formatNumber(engineers.length) },
            { icon: 'ActivityIcon', label: 'Total load', value: formatNumber(sum(engineers, 'total_load')) },
            { icon: 'ClockIcon', label: 'Pending', value: formatNumber(sum(engineers, 'total_pending')) },
          ],
        },
      ]
    },
  },
  mounted() {
    this.getAnalysis()
  },
  methods: {
    openView(key) {
      this.$router.push({ name: `${key}-analysis` })
    },
    getAnalysis() {
      this.loader = true
      this.error = false

      axios.get('/analysis').then(res => {
        const analysis = decryptData(res.data.payload).data?.analysis || {}
        this.requestsAnalysisData = analysis.requests_analysis || []
        this.usersAnalysisData = analysis.users_analysis || []
      }).catch(() => {
        this.error = true
      }).finally(() => {
        this.loader = false
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/pages/analysis/analysis.scss';
</style>
